package com.netguru.multiplatform.charts.line

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.geometry.*
import androidx.compose.ui.platform.*
import androidx.compose.ui.unit.*
import com.netguru.multiplatform.charts.*

@Composable
internal fun LineChartOverlayInformation(
    lineChartData: LineChartData,
    positionX: Float,
    containerSize: Size,
    colors: LineChartColors,
    overlayHeaderLayout: @Composable (value: Long) -> Unit,
    overlayDataEntryLayout: @Composable (dataName: String, value: Float) -> Unit,
) {
    if (positionX < 0) return

    OverlayInformation(
        positionX = positionX,
        containerSize = containerSize,
        surfaceColor = colors.surface
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            val timestampCursor = getTimestampFromCursor(
                xCursorPosition = positionX,
                lineChartData = lineChartData,
                containerSize = containerSize
            )
            val listOfValues = retrieveData(lineChartData, timestampCursor)

            overlayHeaderLayout(timestampCursor)

            Spacer(modifier = Modifier.height(4.dp))

            listOfValues.forEach { seriesAndInterpolatedValue ->
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(16.dp, 4.dp)
                            .drawBehind {
                                drawLine(
                                    strokeWidth = seriesAndInterpolatedValue.lineChartSeries.lineWidth.toPx(),
                                    pathEffect = if (seriesAndInterpolatedValue.lineChartSeries.dashedLine)
                                        dashedPathEffect else null,
                                    color = seriesAndInterpolatedValue.lineChartSeries.lineColor,
                                    start = Offset(0f, size.height / 2),
                                    end = Offset(size.width, size.height / 2)
                                )
                            }
                    )

                    Spacer(modifier = Modifier.width(8.dp))

                    val dataName = seriesAndInterpolatedValue.lineChartSeries.dataName
                    val interpolatedValue = seriesAndInterpolatedValue.interpolatedValue
                    overlayDataEntryLayout(dataName, interpolatedValue)
                }
            }
        }
    }

    // Vertical marker line
    Spacer(
        Modifier
            .offset(
                with(LocalDensity.current) { positionX.toDp() }, 0.dp
            )
            .width(1.dp)
            .fillMaxHeight()
            .background(colors.overlayLine)
    )
}

private fun getTimestampFromCursor(
    xCursorPosition: Float,
    lineChartData: LineChartData,
    containerSize: Size,
) =
    xCursorPosition.toLong().mapValueToDifferentRange(
        0L,
        containerSize.width.toLong(),
        lineChartData.minX,
        lineChartData.maxX
    )

private fun retrieveData(
    lineChartData: LineChartData,
    timestampCursor: Long,
): List<SeriesAndInterpolatedValue> {
    // find time value from position of the cursor

    val outputList: MutableList<SeriesAndInterpolatedValue> = mutableListOf()
    lineChartData.series.forEach { series ->
        // find the point with greater and smaller timestamp
        val v0 = series.listOfPoints
            .filter { it.x >= timestampCursor }
            .minByOrNull { it.x }
        val v1 = series.listOfPoints
            .filter { it.x <= timestampCursor }
            .maxByOrNull { it.x }

        if (v0 != null && v1 != null) {
            val interpolatedValue =
                interpolateBetweenValues(
                    v0.y,
                    v1.y,
                    // (time - v0.timestamp) to bring down the value and avoid Float overflow
                    (timestampCursor - v0.x).toFloat()
                        // t value has to be between 0 and 1
                        .mapValueToDifferentRange(
                            0f,
                            (v1.x - v0.x).toFloat(), // to avoid Float overflow
                            0f,
                            1f
                        )
                )
            outputList.add(SeriesAndInterpolatedValue(series, interpolatedValue))
        }
    }
    return outputList
}

// simple linear interpolation, should be enough for our purpose
// https://en.wikipedia.org/wiki/Linear_interpolation
private fun interpolateBetweenValues(v0: Float, v1: Float, t: Float): Float {
    return v0 + t * (v1 - v0)
}

@Immutable
private data class SeriesAndInterpolatedValue(
    val lineChartSeries: LineChartSeries,
    val interpolatedValue: Float,
)
